import * as React from 'react'
import Hammer from 'hammerjs'
import { useTranslation } from 'hooks'
import { iconPlaceholder, iconArrowLeftL, iconArrowRightL } from 'images'
import ProductPicture from '../ProductPicture'

const DRAG_SENSITIVITY = 25

const ImageSlider: React.FC<Props> = ({
  primaryImageUrl,
  productTitle,
  imageUrls
}) => {
  const { t } = useTranslation()
  const slider = React.useRef<HTMLDivElement>(null)
  const [activeSlideIndex, setActiveSlide] = React.useState(0)
  const slideCount = imageUrls.length
  const nextSlideIndex = (activeSlideIndex + 1) % slideCount
  const prevSlideIndex = !activeSlideIndex
    ? slideCount - 1
    : (activeSlideIndex - 1) % slideCount

  const animateSliderToPercentage = (percentage: number) => {
    if (slider.current) {
      slider.current.style.transform = `translateX(${percentage}%)`
    }
  }

  const goToSlide = (index: number) => {
    animateSliderToPercentage(0 - (100 / slideCount) * index)
    setActiveSlide(index)
  }

  const swipeToSlide = ({
    deltaX,
    isFinal,
    velocityX
  }: {
    deltaX: number
    isFinal: number
    velocityX: number
  }) => {
    const percentage = ((100 / slideCount) * deltaX) / window.innerWidth

    animateSliderToPercentage(
      percentage - (100 / slideCount) * activeSlideIndex
    )

    if (isFinal) {
      const swipeLeftTriggered = velocityX < -1
      const dragLeftTriggered = percentage <= -(DRAG_SENSITIVITY / slideCount)
      const swipeRightTriggered = velocityX > 1
      const dragRightTriggered = percentage >= DRAG_SENSITIVITY / slideCount

      if (swipeRightTriggered || dragRightTriggered) {
        goToSlide(prevSlideIndex)
      } else if (swipeLeftTriggered || dragLeftTriggered) {
        goToSlide(nextSlideIndex)
      } else {
        goToSlide(activeSlideIndex)
      }
    }
  }

  React.useEffect(() => {
    if (slideCount > 1) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      slider.current = document.querySelector('#slider')
      const sliderManager = new Hammer.Manager(slider.current)

      sliderManager.add(new Hammer.Pan({ threshold: 0, pointers: 0 }))
      sliderManager.on('swipeleft swiperight panright panleft', swipeToSlide)

      return () => sliderManager.destroy()
    }
  }, [activeSlideIndex])

  if (primaryImageUrl.indexOf('placeholder') !== -1) {
    return (
      <div className="product-images">
        <img
          alt={productTitle}
          className="product-card__img"
          src={iconPlaceholder}
        />
      </div>
    )
  }

  return (
    <div className="slider">
      <div className="slider__viewport">
        <div
          className="slider__slides"
          id="slider"
          style={{ width: `${slideCount * 100}%` }}
        >
          {imageUrls.map((url, i) => (
            <div
              key={`image_${i}`}
              className={`slide${
                i == activeSlideIndex ? ' slide--active' : ''
              }`}
              {...(i > 0 ? { 'data-nosnippet': true } : {})}
            >
              <ProductPicture
                title={productTitle}
                src={url}
                width={1200}
                lazyLoad={i !== 0}
              />
            </div>
          ))}
        </div>
      </div>
      {slideCount > 1 && (
        <>
          <div className="slider__arrows">
            <button
              className="slider__arrows--arrow"
              onClick={() => goToSlide(prevSlideIndex)}
            >
              <img src={iconArrowLeftL} alt={t('shared.previous')} />
            </button>
            <button
              className="slider__arrows--arrow"
              onClick={() => goToSlide(nextSlideIndex)}
            >
              <img src={iconArrowRightL} alt={t('shared.next')} />
            </button>
          </div>
          <div className="slider__pagination">
            {imageUrls.map((_, i) => (
              <button
                key={`dot_${i}`}
                className={`pagination-dot ${
                  i === activeSlideIndex ? 'is-active' : ''
                }`}
                onClick={() => goToSlide(i)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default ImageSlider

interface Props {
  primaryImageUrl: string
  productTitle: string
  imageUrls: string[]
}
